import { NewProcessModal } from "./ProcessPages";
import { PortalPanel } from "@/ui/portal";
import { RememberedFilterNotice } from "@/ui/filter-notice";
import { useUrlSort } from "@/lib/sort";
import { usePageSize } from "@/lib/pagesize";
import { PersonLgpd } from "./SettingsPlatform";
import { ExportButtons, fetchAllPages } from "@/ui/export";
import { Fragment, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/ui/feedback";
import { api, qs } from "@/lib/api";
import { Link, navigate, useSearch, useTitle } from "@/lib/router";
import { dateOnly, fmtDate } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import {
  Badge, Card, DataTable, EntityPicker, ErrorBox, Field, FormGrid, Loading, Modal, PageHeader, Pager, SearchBox, Tabs, UnitSelect, useAct, type EntityRef, Cnj,
} from "@/ui/kit";
import { ActivityList, NewActivityModal } from "./ActivityPages";
import { DocumentsPanel } from "./shared";
import { GenerateDocButton } from "@/ui/generator";
import { PeriodPicker, usePeriod } from "@/ui/period";
import { useDraft } from "@/lib/draft";
import { useAutofill } from "@/lib/autofill";
import { useFields } from "@/lib/fields";
import { CaptacaoBatchModal } from "@/ui/captacao";
import { OriginHistory, OriginOwnerField, originNeedsReason, type OriginChangeRow } from "@/ui/origin";
import { NewCaseModal } from "./CasePages";
import { AttendancesCard } from "@/ui/attendance";
import { AttendanceInput, emptyAttendance, saveAttendanceDraft, type AttendanceDraft } from "@/ui/attendance";

interface Contact { id?: string; kind: string; value: string; isPrimary: boolean }
interface Address { id?: string; street: string; number: string | null; complement: string | null; district: string | null; city: string; state: string; zip: string | null; isPrimary: boolean }
interface Person {
  id: string; kind: "PF" | "PJ"; name: string; socialName: string | null; document: string | null; rg: string | null;
  birthDate: string | null; nationality: string | null; maritalStatus: string | null; profession: string | null; gender?: string | null;
  notes: string | null; unitId: string; contacts: Contact[]; addresses: Address[];
}
interface PersonDetailT extends Person {
  originKind: string | null; originLabel: string | null; originDetail: string | null; originPersonId: string | null; originPartnerId: string | null;
  originPerson: { id: string; name: string } | null; originPartner: { id: string; name: string } | null; publicDataSource: string | null; publicDataAt: string | null;
  originUserId: string | null; originUser: { id: string; displayName: string } | null; originHistory: OriginChangeRow[]; canChangeOrigin: boolean;
  cases: Array<{ id: string; role: string; case: { id: string; number: number; title: string; status: string } }>;
  processes: Array<{ id: string; role: string; process: { id: string; cnjNumber: string | null; title: string | null } }>;
}

export function fmtDoc(d: string | null | undefined): string {
  if (!d) return "—";
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  return d;
}

export function PeoplePage(): JSX.Element {
  useTitle("Pessoas");
  const [search, set] = useSearch();
  const q = search.get("q") ?? "";
  const skip = Number(search.get("skip") ?? 0);
  const origem = search.get("origem") ?? "";
  const [creating, setCreating] = useState(search.get("novo") === "1");
  const [showOrigins, setShowOrigins] = useState(false);
  const [batchOrigin, setBatchOrigin] = useState(false);
  const { can } = useLookups();
  const pageSize = usePageSize();
  const srv = useUrlSort();
  const { data, error } = useQuery({
    queryKey: ["pessoas", q, skip, origem, pageSize, srv.params],
    queryFn: () => api.get<{ items: Array<Person & { contacts: Contact[]; originKind: string | null }>; total: number; take: number; skip: number }>(`/pessoas${qs({ q, skip, take: pageSize, origem, ...srv.params })}`),
  });
  return (
    <div>
      <PageHeader title="Pessoas" subtitle="Clientes, partes, representantes e contatos"
        actions={<>
          {(can("config.gerenciar") || can("financeiro.gerente")) && <button className="btn" onClick={() => setBatchOrigin(true)}>Captação em lote</button>}
          {can("pessoa.editar") && <button className="btn primary" onClick={() => setCreating(true)}>Nova pessoa</button>}
        </>} />
      {batchOrigin && <CaptacaoBatchModal onClose={() => setBatchOrigin(false)} />}
      <RememberedFilterNotice screen="pessoas" keys={["origem"]} onClear={() => set({ skip: null })}
        labels={{ origem: (v) => ["Origem", v === "sem" ? "não informada" : ORIGINS.find((o) => o.key === v)?.label ?? v] }} />
      <div className="toolbar"><SearchBox value={q} onChange={(v) => set({ q: v, skip: null })} placeholder="Nome, CPF ou CNPJ…" />
        <select className="input" style={{ width: "auto" }} value={origem} onChange={(e) => set({ origem: e.target.value || null, skip: null })}>
          <option value="">Todas as origens</option>{ORIGINS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}<option value="sem">Origem não informada</option>
        </select>
        <button className="btn small ghost" onClick={() => setShowOrigins(!showOrigins)}>{showOrigins ? "Ocultar" : "Ver"} resumo por origem</button>
        <span className="spacer" />
        <ExportButtons title="Pessoas" load={async () => {
          const all = await fetchAllPages<Person & { contacts: Contact[]; originKind: string | null }>((sk, tk) => `/pessoas${qs({ q, skip: sk, take: tk, origem, ...srv.params })}`);
          return { columns: ["Nome", "Tipo", "CPF/CNPJ", "E-mail", "Telefone", "Origem"],
            rows: all.map((p) => [p.name, p.kind, fmtDoc(p.document), p.contacts.find((c) => c.kind === "email")?.value ?? "", p.contacts.find((c) => c.kind !== "email")?.value ?? "", ORIGINS.find((o) => o.key === p.originKind)?.label ?? ""]) };
        }} />
      </div>
      {showOrigins && <OriginSummary />}
      <Card>
        <ErrorBox error={error} />
        <DataTable rows={data?.items} rowKey={(p) => p.id} onRowClick={(p) => navigate(`/pessoas/${p.id}`)} server={srv}
          columns={[
            { key: "n", label: "Nome", sortKey: "nome", render: (p) => <>{p.name}{p.socialName && <div className="muted small">{p.socialName}</div>}</> },
            { key: "k", label: "Tipo", width: 60, sortKey: "tipo", render: (p) => <Badge>{p.kind}</Badge> },
            { key: "d", label: "CPF/CNPJ", width: 170, sortKey: "documento", render: (p) => <span className="mono">{fmtDoc(p.document)}</span> },
            { key: "c", label: "Contato principal", render: (p) => p.contacts[0]?.value ?? "—" },
            { key: "o", label: "Origem", width: 150, sortKey: "origem", render: (p) => ORIGINS.find((o) => o.key === p.originKind)?.label ?? <span className="muted small">—</span> },
          ]} />
        {data && <Pager total={data.total} take={data.take} skip={data.skip} onChange={(s) => set({ skip: String(s) })} />}
      </Card>
      {creating && <PersonForm onClose={() => setCreating(false)} onSaved={(id) => navigate(`/pessoas/${id}`)} />}
    </div>
  );
}

function OriginSummary(): JSX.Element {
  const [period, setPeriod] = usePeriod("ano");
  const { data } = useQuery({ queryKey: ["pessoas-origens", period.from, period.to], queryFn: () => api.get<Array<{ originKind: string | null; label: string; total: number; clientes: number }>>(`/pessoas/origens/resumo${qs({ from: period.from, to: period.to })}`) });
  const max = Math.max(1, ...(data ?? []).map((r) => r.total));
  return (
    <Card title="De onde vêm os cadastros" actions={<PeriodPicker value={period} onChange={setPeriod} />}>
      {!data ? <Loading /> : (
        <table className="mini-table" style={{ width: "100%" }}>
          <thead><tr><th style={{ textAlign: "left" }}>Origem</th><th>Cadastros</th><th>Viraram cliente</th><th /></tr></thead>
          <tbody>{data.filter((r) => r.total).map((r) => (
            <tr key={r.label}><td>{r.label}</td><td style={{ textAlign: "right" }}>{r.total}</td><td style={{ textAlign: "right" }}>{r.clientes}</td>
              <td style={{ width: "40%" }}><div style={{ background: "var(--primary)", opacity: 0.7, height: 8, borderRadius: 4, width: `${(r.total / max) * 100}%` }} /></td></tr>
          ))}</tbody>
        </table>
      )}
      <div className="muted small pad">Cadastros criados no período, pela origem informada. "Viraram cliente" = estão como cliente em algum caso.</div>
    </Card>
  );
}

export const ORIGINS: Array<{ key: string; label: string; detail?: string }> = [
  { key: "indicacao", label: "Indicação" },
  { key: "parceria", label: "Parceria" },
  { key: "dativo", label: "Dativo (nomeação)", detail: "Nomeação / processo / convênio" },
  { key: "trafego_pago", label: "Tráfego pago", detail: "Canal ou campanha (Google, Meta…)" },
  { key: "rede_social", label: "Rede social", detail: "Qual rede" },
  { key: "proprio", label: "Próprio (procurou o escritório)", detail: "Como chegou (site, placa, já conhecia…)" },
  { key: "outro", label: "Outro", detail: "Qual" },
];
const REDES = ["Instagram", "Facebook", "TikTok", "YouTube", "LinkedIn", "WhatsApp", "Outra"];

interface DocLookup {
  document: string; kind: "PF" | "PJ"; existing: { id: string; name: string } | null; warning: string | null; cpfLookupAvailable: boolean;
  found: null | {
    kind: "PF" | "PJ"; name: string; source: string; status: string | null; birthDate?: string | null;
    tradeName?: string | null; statusDate?: string | null; openedAt?: string | null; legalNature?: string | null; mainActivity?: string | null; size?: string | null;
    email?: string | null; phones?: string[]; address?: { street: string; number?: string; complement: string; district: string; city: string; state: string; zip: string } | null;
    partners?: Array<{ name: string; role: string; since: string | null }>;
  };
}
const onlyDigits = (s: string) => s.replace(/\D/g, "");
function maskDoc(v: string): string {
  const d = onlyDigits(v).slice(0, 14);
  if (d.length <= 11) return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})\.(\d{3})(\d)/, "$1.$2.$3").replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
  return d.replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");
}
const maskCep = (v: string) => onlyDigits(v).slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");
const fmtPhone = (d: string) => (d.length === 11 ? `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}` : d.length === 10 ? `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}` : d);

/** "Quem indicou": uma busca só em Pessoas e Parceiros, com cadastro na hora. */
function ReferrerPicker({ value, partners, onChange }: {
  value: { kind: "pessoa" | "parceiro"; id: string; label: string } | null;
  partners: Array<{ id: string; name: string }>;
  onChange: (v: { kind: "pessoa" | "parceiro"; id: string; label: string } | null) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const { can } = useLookups();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [newPerson, setNewPerson] = useState(false);
  const [newPartner, setNewPartner] = useState<string | null>(null);
  const { data: people } = useQuery({
    queryKey: ["indicacao-busca", q], enabled: q.trim().length >= 2,
    queryFn: () => api.get<{ items: Array<{ id: string; name: string; document: string | null }> }>(`/pessoas${qs({ q: q.trim(), take: 6 })}`),
  });
  const pts = q.trim().length >= 2 ? partners.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 6) : [];
  if (value) return <div className="row nowrap"><span className="chip">{value.kind === "parceiro" ? "🤝 Parceiro" : "👤 Pessoa"} · {value.label}</span><button className="link-btn small" onClick={() => onChange(null)}>trocar</button></div>;
  return (
    <div style={{ position: "relative" }}>
      <input className="input" value={q} placeholder="Digite o nome de quem indicou…" onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {open && q.trim().length >= 2 && (
        <div className="combo-pop">
          {(people?.items ?? []).map((p) => <button key={p.id} type="button" onMouseDown={() => onChange({ kind: "pessoa", id: p.id, label: p.name })}>👤 {p.name}{p.document ? <span className="muted small"> · {fmtDoc(p.document)}</span> : null}</button>)}
          {pts.map((p) => <button key={p.id} type="button" onMouseDown={() => onChange({ kind: "parceiro", id: p.id, label: p.name })}>🤝 {p.name} <span className="muted small">· parceiro</span></button>)}
          {!(people?.items.length) && !pts.length && <div className="muted small pad">Ninguém encontrado com “{q}”.</div>}
          <div className="row" style={{ padding: 6, borderTop: "1px solid var(--border-l)" }}>
            <button type="button" className="btn small" onMouseDown={() => setNewPerson(true)}>+ Cadastrar pessoa</button>
            {can("financeiro.lancar") && <button type="button" className="btn small" onMouseDown={() => setNewPartner(q.trim())}>+ Cadastrar parceiro</button>}
          </div>
        </div>
      )}
      {newPerson && <PersonForm onClose={() => setNewPerson(false)} onSaved={async (id) => { const p = await api.get<{ id: string; name: string }>(`/pessoas/${id}`); onChange({ kind: "pessoa", id: p.id, label: p.name }); }} />}
      {newPartner !== null && (
        <Modal title="Cadastrar parceiro" onClose={() => setNewPartner(null)} footer={<>
          <button className="btn" onClick={() => setNewPartner(null)}>Cancelar</button>
          <button className="btn primary" disabled={newPartner.trim().length < 2} onClick={async () => {
            try {
              const r = await api.post<{ id: string }>("/financeiro/parceiros", { name: newPartner.trim() });
              await qc.invalidateQueries({ queryKey: ["parceiros-ativos"] });
              onChange({ kind: "parceiro", id: r.id, label: newPartner.trim() }); setNewPartner(null);
            } catch (e) { toast.error((e as Error).message); }
          }}>Cadastrar</button></>}>
          <Field label="Nome do parceiro"><input className="input" autoFocus value={newPartner} onChange={(e) => setNewPartner(e.target.value)} /></Field>
          <div className="muted small pad">Os demais dados (OAB, regras de repasse) ficam em Financeiro › Parceiros.</div>
        </Modal>
      )}
    </div>
  );
}

export function PersonForm({ person, onClose, onSaved }: { person?: Person & { originKind?: string | null; originDetail?: string | null; originPersonId?: string | null; originPartnerId?: string | null; originPerson?: { id: string; name: string } | null; originUserId?: string | null; canChangeOrigin?: boolean }; onClose: () => void; onSaved?: (id: string) => void }): JSX.Element {
  const [f, setF] = useState({
    kind: person?.kind ?? "PF", name: person?.name ?? "", socialName: person?.socialName ?? "", document: person?.document ? maskDoc(person.document) : "",
    rg: person?.rg ?? "", birthDate: dateOnly(person?.birthDate), nationality: person?.nationality ?? "",
    maritalStatus: person?.maritalStatus ?? "", profession: person?.profession ?? "", notes: person?.notes ?? "", gender: person?.gender ?? "",
    unitId: person?.unitId ?? null as string | null,
    originKind: person?.originKind ?? "", originDetail: person?.originDetail ?? "", originPartnerId: person?.originPartnerId ?? "",
    originUserId: person?.originUserId ?? (null as string | null), originReason: "",
  });
  const [originPerson, setOriginPerson] = useState<EntityRef | null>(person?.originPerson ? { kind: "pessoa", id: person.originPerson.id, label: person.originPerson.name } : null);
  const [contacts, setContacts] = useState<Contact[]>(person?.contacts.length ? person.contacts.map(({ kind, value, isPrimary }) => ({ kind, value, isPrimary })) : [{ kind: "telefone", value: "", isPrimary: true }]);
  // Endereço já aberto no cadastro; o "+" é só para um segundo endereço.
  const EMPTY_ADDR: Address = { street: "", number: "", complement: "", district: "", city: "", state: "", zip: "", isPrimary: true };
  const [addr, setAddr] = useState<Address | null>(person?.addresses[0] ? { ...person.addresses[0] } : EMPTY_ADDR);
  const [addr2, setAddr2] = useState<Address | null>(person?.addresses[1] ? { ...person.addresses[1] } : null);
  const [lookup, setLookup] = useState<DocLookup | null>(null);
  const [docErr, setDocErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<"" | "doc" | "cep">("");
  const [cepMsg, setCepMsg] = useState<string | null>(null);
  const af = useAutofill();
  const { data: partners } = useQuery({ queryKey: ["parceiros-ativos"], queryFn: () => api.get<Array<{ id: string; name: string }>>("/financeiro/parceiros/ativos") });
  const up = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const F = useFields("pessoa");
  const draft = useDraft(`pessoa:${person?.id ?? "nova"}`, { f, contacts, addr, originPerson }, (v) => { setF(v.f); setContacts(v.contacts); setAddr(v.addr); setOriginPerson(v.originPerson); });

  /** Consulta o documento: valida, avisa duplicado e preenche o que estiver vazio. */
  const searchDoc = async (raw: string) => {
    const d = onlyDigits(raw);
    if (d.length !== 11 && d.length !== 14) return;
    if (person?.document === d) return;
    setBusy("doc"); setDocErr(null);
    try {
      const r = await api.get<DocLookup>(`/consultas/documento/${d}`);
      setLookup(r);
      const fd = r.found;
      if (fd) {
        const got: Array<[string, string, unknown, unknown]> = [
          ["name", fd.kind === "PJ" ? "razão social" : "nome", fd.name, f.name], ["socialName", "nome fantasia", fd.tradeName, f.socialName],
          ["birthDate", "nascimento", fd.birthDate, f.birthDate],
        ];
        const addrOk = !!fd.address && !(addr && addr.street);
        const hasContacts = !!(fd.phones?.length || fd.email);
        const filled = got.filter(([, , v, cur]) => v && !cur);
        af.mark([...filled.map(([k]) => k), ...(addrOk ? ["street", "number", "complement", "district", "city", "state"] : []), ...(hasContacts ? ["contact"] : [])],
          [...filled.map(([, l]) => l), ...(addrOk ? ["endereço"] : []), ...(hasContacts ? ["contatos"] : [])]);
      }
      setF((x) => ({
        ...x, kind: r.kind,
        name: x.name || fd?.name || "",
        socialName: x.socialName || fd?.tradeName || "",
        birthDate: x.birthDate || (fd?.birthDate ?? ""),
      }));
      if (fd) {
        setContacts((cs) => {
          const have = new Set(cs.map((c) => onlyDigits(c.value) || c.value.toLowerCase()));
          const extra: Contact[] = [
            ...(fd.phones ?? []).filter((p) => !have.has(p)).map((p) => ({ kind: "telefone", value: fmtPhone(p), isPrimary: false })),
            ...(fd.email && !have.has(fd.email) ? [{ kind: "email", value: fd.email, isPrimary: false }] : []),
          ];
          const kept = cs.filter((c) => c.value.trim());
          return kept.length || extra.length ? [...kept, ...extra] : cs;
        });
        if (fd.address) setAddr((a) => (a && a.street ? a : { street: fd.address!.street, number: fd.address!.number ?? "", complement: fd.address!.complement, district: fd.address!.district, city: fd.address!.city, state: fd.address!.state, zip: fd.address!.zip, isPrimary: true }));
      }
    } catch (e) {
      setLookup(null); setDocErr((e as Error).message);
    } finally { setBusy(""); }
  };

  const searchCep = async (raw: string) => {
    const cep = onlyDigits(raw);
    if (cep.length !== 8) return;
    setBusy("cep"); setCepMsg(null);
    try {
      const a = await api.get<{ street: string; district: string; city: string; state: string; zip: string; complement: string }>(`/consultas/cep/${cep}`);
      setAddr((x) => ({ ...(x ?? { number: "", complement: "", isPrimary: true } as Address), street: a.street || x?.street || "", district: a.district || x?.district || "", city: a.city, state: a.state, zip: cep, complement: x?.complement || a.complement || "" } as Address));
      af.mark(["street", "district", "city", "state"].filter((k) => (a as Record<string, string>)[k]), ["endereço pelo CEP"]);
      if (!a.street) setCepMsg("CEP geral da cidade: preencha o logradouro.");
      setTimeout(() => document.getElementById("addr-number")?.focus(), 50);
    } catch (e) { setCepMsg((e as Error).message); } finally { setBusy(""); }
  };

  const dup = lookup?.existing && lookup.existing.id !== (person as { id?: string } | undefined)?.id ? lookup.existing : null;
  const pf = f.kind === "PF";
  const missing = F.missing({
    document: f.document, socialName: f.socialName, ...(pf ? { rg: f.rg, birthDate: f.birthDate, maritalStatus: f.maritalStatus, nationality: f.nationality, profession: f.profession, gender: f.gender } : {}),
    contacts: contacts.filter((c) => c.value.trim()), address: addr && addr.street && addr.city ? [addr] : [], originKind: f.originKind, originUserId: f.originUserId, notes: f.notes,
  });
  if (originNeedsReason(person?.originUserId, f.originUserId) && f.originReason.trim().length < 3) missing.push("motivo da troca do captador");
  const originDef = ORIGINS.find((o) => o.key === f.originKind);
  const [att, setAtt] = useState<AttendanceDraft>(emptyAttendance());
  const save = useAct(() => {
    const body = {
      kind: f.kind, name: f.name, socialName: f.socialName || null, document: onlyDigits(f.document) || null, rg: f.rg || null,
      birthDate: f.birthDate || null, nationality: f.nationality || null, maritalStatus: f.maritalStatus || null,
      profession: f.profession || null, gender: f.kind === "PF" ? f.gender || null : null, notes: f.notes || null, ...(f.unitId ? { unitId: f.unitId } : {}),
      contacts: contacts.filter((c) => c.value.trim()).map((c, i) => ({ ...c, isPrimary: i === 0 })),
      addresses: [
        ...(addr && addr.street && addr.city ? [{ ...addr, zip: addr.zip ? onlyDigits(addr.zip) : null, id: undefined, isPrimary: true }] : []),
        ...(addr2 && addr2.street && addr2.city ? [{ ...addr2, zip: addr2.zip ? onlyDigits(addr2.zip) : null, id: undefined, isPrimary: false }] : []),
      ],
      originKind: f.originKind || null, originDetail: f.originDetail || null,
      originPersonId: f.originKind === "indicacao" ? originPerson?.id ?? null : null,
      originPartnerId: f.originKind === "parceria" ? f.originPartnerId || null : null,
      ...(!person || (person.originUserId ?? null) !== f.originUserId ? { originUserId: f.originUserId || null } : {}),
      ...(originNeedsReason(person?.originUserId, f.originUserId) ? { originChangeReason: f.originReason } : {}),
      ...(lookup?.found ? { publicData: lookup.found as unknown as Record<string, unknown>, publicDataSource: lookup.found.source.slice(0, 40) } : {}),
    };
    return person ? api.patch<{ id: string }>(`/pessoas/${person.id}`, body) : api.post<{ id: string }>("/pessoas", body).then(async (r) => { await saveAttendanceDraft({ personId: r.id }, att); return r; });
  }, [["pessoas"], ["pessoa"], ["atendimentos"]], (r) => { draft.clear(); onClose(); onSaved?.(r.id); });

  const fd = lookup?.found;
  return (
    <Modal title={person ? "Editar pessoa" : "Nova pessoa"} onClose={onClose} wide footer={
      <><button className="btn" onClick={onClose}>Cancelar</button>
        {missing.length > 0 && <span className="muted small" style={{ marginRight: "auto" }}>Falta: {missing.join(", ")}</span>}
        <button className="btn primary" disabled={save.isPending || f.name.trim().length < 2 || !!dup || missing.length > 0} onClick={() => save.mutate()}>Salvar</button></>
    }>
      <div className="stack" onInputCapture={af.onInput} onChangeCapture={af.onInput}>
        {draft.banner}
        <FormGrid cols={3}>
          <Field label="CPF ou CNPJ" required={F.req("document")} hint={busy === "doc" ? "Consultando…" : "Digite e os dados públicos são buscados automaticamente"}>
            <div className="row nowrap">
              <input className="input mono" value={f.document} autoFocus={!person} inputMode="numeric" placeholder="000.000.000-00"
                onChange={(e) => { const v = maskDoc(e.target.value); up({ document: v }); setDocErr(null); const d = onlyDigits(v); if (d.length === 11 || d.length === 14) void searchDoc(v); else setLookup(null); }} />
              <button className="btn small" disabled={busy === "doc"} onClick={() => void searchDoc(f.document)}>Buscar</button>
            </div>
          </Field>
          <Field label="Tipo" required>
            <select className="input" value={f.kind} onChange={(e) => up({ kind: e.target.value as "PF" | "PJ" })}>
              <option value="PF">Pessoa física</option><option value="PJ">Pessoa jurídica</option>
            </select>
          </Field>
          <Field label="Unidade"><UnitSelect value={f.unitId} onChange={(v) => up({ unitId: v })} allowEmpty /></Field>
        </FormGrid>
        {docErr && <div className="text-bad small">{docErr}</div>}
        {dup && <div className="charges-box late small">Já existe um cadastro com este documento: <Link to={`/pessoas/${dup.id}`} onClick={onClose}><strong>{dup.name}</strong></Link>. Abra o existente em vez de criar outro.</div>}
        {lookup?.warning && !dup && <div className="muted small">{lookup.warning}</div>}
        {af.summary(lookup?.found ? lookup.found.source : "CEP")}
        {fd && (
          <div className="charges-box small">
            <strong>Dados públicos encontrados</strong> <span className="muted">({fd.source}) — conferidos e preenchidos abaixo onde estava vazio</span>
            <div>{fd.status && <>Situação: <strong className={/ativa|regular/i.test(fd.status) ? "text-ok" : "text-bad"}>{fd.status}</strong></>}{fd.openedAt && <> · Abertura {fmtDate(fd.openedAt)}</>}{fd.size && <> · {fd.size}</>}{fd.legalNature && <> · {fd.legalNature}</>}</div>
            {fd.mainActivity && <div>Atividade: {fd.mainActivity}</div>}
            {!!fd.partners?.length && <div>Sócios: {fd.partners.map((p) => `${p.name}${p.role ? ` (${p.role})` : ""}`).join("; ")}</div>}
          </div>
        )}
        <FormGrid cols={3}>
          <Field label={f.kind === "PF" ? "Nome completo" : "Razão social"} required span={2}><input className={af.cls("name")} data-af="name" value={f.name} onChange={(e) => up({ name: e.target.value })} autoFocus={!!person} /></Field>
          {F.show("socialName") && <Field label={f.kind === "PF" ? "Nome social" : "Nome fantasia"} required={F.req("socialName")}><input className={af.cls("socialName")} data-af="socialName" value={f.socialName} onChange={(e) => up({ socialName: e.target.value })} /></Field>}
          {f.kind === "PF" && (
            <>
              {F.show("rg") && <Field required={F.req("rg")} label="RG"><input className="input" value={f.rg} onChange={(e) => up({ rg: e.target.value })} /></Field>}
              {F.show("birthDate") && <Field required={F.req("birthDate")} label="Nascimento"><input type="date" className={af.cls("birthDate")} data-af="birthDate" value={f.birthDate} onChange={(e) => up({ birthDate: e.target.value })} /></Field>}
              {F.show("maritalStatus") && <Field required={F.req("maritalStatus")} label="Estado civil">
                <select className="input" value={f.maritalStatus} onChange={(e) => up({ maritalStatus: e.target.value })}>
                  <option value="">—</option>
                  {["solteiro(a)", "casado(a)", "união estável", "divorciado(a)", "separado(a)", "viúvo(a)"].map((x) => <option key={x}>{x}</option>)}
                </select>
              </Field>}
              {F.show("nationality") && <Field required={F.req("nationality")} label="Nacionalidade"><input className="input" value={f.nationality} onChange={(e) => up({ nationality: e.target.value })} placeholder="brasileira" /></Field>}
              {F.show("gender") && <Field required={F.req("gender")} label="Gênero" hint="Para a concordância nos documentos (portador/portadora…)">
                <select className="input" value={f.gender} onChange={(e) => up({ gender: e.target.value })}><option value="">Não informado</option><option value="F">Feminino</option><option value="M">Masculino</option></select>
              </Field>}
              {F.show("profession") && <Field required={F.req("profession")} label="Profissão"><input className="input" value={f.profession} onChange={(e) => up({ profession: e.target.value })} /></Field>}
            </>
          )}
        </FormGrid>

        {F.show("originKind") && <><div className="fld-label">Origem{F.req("originKind") && <span className="req"> *</span>}</div>
        <FormGrid cols={3}>
          <Field label="Como chegou ao escritório">
            <select className="input" value={f.originKind} onChange={(e) => up({ originKind: e.target.value, originDetail: "" })}>
              <option value="">Não informada</option>
              {ORIGINS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </Field>
          {(f.originKind === "indicacao" || f.originKind === "parceria") && <Field label="Quem indicou" span={2} hint="Busca em pessoas e parceiros. Não achou? Cadastre na hora.">
            <ReferrerPicker
              value={f.originKind === "parceria" ? (f.originPartnerId ? { kind: "parceiro", id: f.originPartnerId, label: partners?.find((x) => x.id === f.originPartnerId)?.name ?? "Parceiro" } : null) : originPerson ? { kind: "pessoa", id: originPerson.id, label: originPerson.label } : null}
              partners={partners ?? []}
              onChange={(v) => {
                if (!v) { setOriginPerson(null); up({ originPartnerId: "" }); return; }
                if (v.kind === "parceiro") { setOriginPerson(null); up({ originKind: "parceria", originPartnerId: v.id }); }
                else { setOriginPerson({ kind: "pessoa", id: v.id, label: v.label }); up({ originKind: "indicacao", originPartnerId: "" }); }
              }} />
          </Field>}
          {f.originKind === "rede_social" && <Field label="Qual rede" span={2}><select className="input" value={f.originDetail} onChange={(e) => up({ originDetail: e.target.value })}><option value="">—</option>{REDES.map((r) => <option key={r}>{r}</option>)}</select></Field>}
          {originDef?.detail && f.originKind !== "rede_social" && <Field label={originDef.detail} span={2}><input className="input" value={f.originDetail} onChange={(e) => up({ originDetail: e.target.value })} /></Field>}
          {(f.originKind === "indicacao" || f.originKind === "parceria") && <Field label="Observação" span={3}><input className="input" value={f.originDetail} onChange={(e) => up({ originDetail: e.target.value })} placeholder="opcional" /></Field>}
        </FormGrid></>}
        {F.show("originUserId") && (
          <FormGrid cols={3}>
            <OriginOwnerField value={f.originUserId} original={person?.originUserId} canChange={person?.canChangeOrigin} required={F.req("originUserId")}
              onChange={(v) => up({ originUserId: v })} reason={f.originReason} onReason={(v) => up({ originReason: v })}
              hint="Quem do escritório trouxe o cliente. Os casos novos dele herdam este nome." />
          </FormGrid>
        )}

        {F.show("contacts") && <><div className="fld-label">Contatos {F.req("contacts") && <span className="req">*</span>}</div>
        {contacts.map((c, i) => (
          <div key={i} className="row nowrap">
            <select className="input" style={{ width: 140 }} value={c.kind} onChange={(e) => setContacts(contacts.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))}>
              <option value="telefone">Telefone</option><option value="whatsapp">WhatsApp</option><option value="email">E-mail</option>
            </select>
            <input className={af.cls("contact")} data-af="contact" value={c.value} onChange={(e) => setContacts(contacts.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
            {contacts.length > 1 && <button className="btn small" onClick={() => setContacts(contacts.filter((_, j) => j !== i))}>Remover</button>}
          </div>
        ))}
        <div><button className="btn small" onClick={() => setContacts([...contacts, { kind: "email", value: "", isPrimary: false }])}>+ contato</button></div></>}

        {F.show("address") && <><div className="fld-label">Endereço {F.req("address") && <span className="req">*</span>}</div>
        {addr ? (
          <FormGrid cols={3}>
            <Field label="CEP" hint={busy === "cep" ? "Buscando…" : cepMsg ?? "Digite o CEP e o endereço é preenchido"}>
              <input className="input mono" value={addr.zip ? maskCep(addr.zip) : ""} inputMode="numeric" placeholder="00000-000"
                onChange={(e) => { const v = maskCep(e.target.value); setAddr({ ...addr, zip: v }); if (onlyDigits(v).length === 8) void searchCep(v); }} />
            </Field>
            <Field label="Logradouro" span={2}><input className={af.cls("street")} data-af="street" value={addr.street} onChange={(e) => setAddr({ ...addr, street: e.target.value })} /></Field>
            <Field label="Número"><input id="addr-number" className={af.cls("number")} data-af="number" value={addr.number ?? ""} onChange={(e) => setAddr({ ...addr, number: e.target.value })} /></Field>
            <Field label="Complemento"><input className={af.cls("complement")} data-af="complement" value={addr.complement ?? ""} onChange={(e) => setAddr({ ...addr, complement: e.target.value })} /></Field>
            <Field label="Bairro"><input className={af.cls("district")} data-af="district" value={addr.district ?? ""} onChange={(e) => setAddr({ ...addr, district: e.target.value })} /></Field>
            <Field label="Cidade" span={2}><input className={af.cls("city")} data-af="city" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} /></Field>
            <Field label="UF"><input className={af.cls("state")} data-af="state" maxLength={2} value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value.toUpperCase() })} /></Field>
          </FormGrid>
        ) : <div><button className="btn small" onClick={() => setAddr({ ...EMPTY_ADDR })}>+ endereço</button></div>}
        {addr && (addr2 ? (
          <>
            <div className="fld-label">Segundo endereço <button className="link-btn small" onClick={() => setAddr2(null)}>tirar</button></div>
            <FormGrid cols={3}>
              <Field label="CEP"><input className="input mono" value={addr2.zip ? maskCep(addr2.zip) : ""} inputMode="numeric" placeholder="00000-000" onChange={(e) => setAddr2({ ...addr2, zip: maskCep(e.target.value) })} /></Field>
              <Field label="Logradouro" span={2}><input className="input" value={addr2.street} onChange={(e) => setAddr2({ ...addr2, street: e.target.value })} /></Field>
              <Field label="Número"><input className="input" value={addr2.number ?? ""} onChange={(e) => setAddr2({ ...addr2, number: e.target.value })} /></Field>
              <Field label="Complemento"><input className="input" value={addr2.complement ?? ""} onChange={(e) => setAddr2({ ...addr2, complement: e.target.value })} /></Field>
              <Field label="Bairro"><input className="input" value={addr2.district ?? ""} onChange={(e) => setAddr2({ ...addr2, district: e.target.value })} /></Field>
              <Field label="Cidade" span={2}><input className="input" value={addr2.city} onChange={(e) => setAddr2({ ...addr2, city: e.target.value })} /></Field>
              <Field label="UF"><input className="input" maxLength={2} value={addr2.state} onChange={(e) => setAddr2({ ...addr2, state: e.target.value.toUpperCase() })} /></Field>
            </FormGrid>
          </>
        ) : <div><button className="btn small" onClick={() => setAddr2({ ...EMPTY_ADDR, isPrimary: false })}>+ outro endereço</button></div>)}</>}

        {F.show("notes") && <Field label="Observações" required={F.req("notes")}><textarea className="input" rows={2} value={f.notes} onChange={(e) => up({ notes: e.target.value })} /></Field>}
        {!person && <AttendanceInput value={att} onChange={setAtt} />}
        <ErrorBox error={save.error} />
      </div>
    </Modal>
  );
}

export function PersonDetail({ id }: { id: string }): JSX.Element {
  const { data: p, error } = useQuery({ queryKey: ["pessoa", id], queryFn: () => api.get<PersonDetailT>(`/pessoas/${id}`) });
  useTitle(p?.name ?? "Pessoa");
  const [tab, setTab] = useState<"dados" | "atividades" | "documentos" | "portal">("dados");
  const [dlg, setDlg] = useState<null | "edit" | "case" | "act" | "process">(null);
  const { can, unitName } = useLookups();
  if (error) return <ErrorBox error={error} />;
  if (!p) return <Loading />;
  return (
    <div className="stack gap-16">
      <PageHeader title={p.name} subtitle={<>{p.kind === "PF" ? "Pessoa física" : "Pessoa jurídica"} · {fmtDoc(p.document)} · {unitName(p.unitId)}</>}
        actions={<>
          {can("pessoa.editar") && <button className="btn" onClick={() => setDlg("edit")}>Editar</button>}
          {can("documento.enviar") && <GenerateDocButton ctx={{ personId: p.id }} />}
          <button className="btn" onClick={() => setDlg("act")}>Nova atividade</button>
          {can("processo.editar") && <button className="btn" onClick={() => setDlg("process")}>Novo processo</button>}
          {can("caso.editar") && <button className="btn primary" onClick={() => setDlg("case")}>Novo caso</button>}
          {can("config.gerenciar") && <PersonLgpd personId={p.id} name={p.name} />}
        </>} />
      <Tabs value={tab} onChange={setTab} tabs={[{ key: "dados", label: "Dados" }, { key: "atividades", label: "Atividades" }, { key: "documentos", label: "Documentos", hidden: !can("documento.ver") }, { key: "portal", label: "Portal do cliente" }]} />
      {tab === "portal" && <PortalPanel scope="pessoa" id={p.id} canEdit={can("pessoa.editar")} />}
      {tab === "dados" && (
        <div className="grid side">
          <div className="stack gap-16">
            <Card title="Casos">
              <DataTable rows={p.cases} rowKey={(c) => c.id} onRowClick={(c) => navigate(`/casos/${c.case.id}`)} empty="Nenhum caso."
                columns={[
                  { key: "n", label: "Caso", render: (c) => <>#{c.case.number} {c.case.title}</> },
                  { key: "r", label: "Papel", render: (c) => c.role },
                  { key: "s", label: "Situação", render: (c) => <Badge tone={c.case.status === "ativo" ? "ok" : "muted"}>{c.case.status}</Badge> },
                ]} />
            </Card>
            <AttendancesCard personId={p.id} canEdit={can("pessoa.editar")} />
            <Card title="Processos">
              <DataTable rows={p.processes} rowKey={(x) => x.id} onRowClick={(x) => navigate(`/processos/${x.process.id}`)} empty="Nenhum processo."
                columns={[
                  { key: "n", label: "Processo", render: (x) => x.process.cnjNumber ? <Cnj value={x.process.cnjNumber} /> : <span>{x.process.title}</span> },
                  { key: "r", label: "Papel", render: (x) => x.role },
                ]} />
            </Card>
          </div>
          <Card title="Cadastro">
            <dl className="kv">
              {p.socialName && (<><dt>{p.kind === "PF" ? "Nome social" : "Fantasia"}</dt><dd>{p.socialName}</dd></>)}
              <dt>Origem</dt><dd>{p.originLabel ?? <span className="muted">não informada</span>}{p.originPerson && <> — <Link to={`/pessoas/${p.originPerson.id}`}>{p.originPerson.name}</Link></>}{p.originPartner && <> — {p.originPartner.name}</>}{p.originDetail && <> — {p.originDetail}</>}</dd>
              <dt>Captado por</dt><dd>{p.originUser?.displayName ?? <span className="muted">não informado</span>}<OriginHistory rows={p.originHistory ?? []} /></dd>
              {p.kind === "PF" && (<>
                <dt>RG</dt><dd>{p.rg ?? "—"}</dd>
                <dt>Nascimento</dt><dd>{fmtDate(p.birthDate)}</dd>
                <dt>Estado civil</dt><dd>{p.maritalStatus ?? "—"}</dd>
                <dt>Profissão</dt><dd>{p.profession ?? "—"}</dd>
                <dt>Nacionalidade</dt><dd>{p.nationality ?? "—"}</dd>
              </>)}
              {p.contacts.map((c, i) => <Fragment key={`c${i}`}><dt>{c.kind}</dt><dd>{c.value}</dd></Fragment>)}
              {p.addresses.map((a, i) => <Fragment key={`a${i}`}><dt>Endereço</dt><dd>{a.street}{a.number ? `, ${a.number}` : ""}{a.complement ? ` – ${a.complement}` : ""}<br />{a.district ? `${a.district} – ` : ""}{a.city}/{a.state} {a.zip ?? ""}</dd></Fragment>)}
            </dl>
            {p.notes && <><hr className="sep" /><div className="pre small">{p.notes}</div></>}
            {p.publicDataSource && <div className="muted small" style={{ marginTop: 8 }}>Dados conferidos em {p.publicDataSource}{p.publicDataAt ? ` em ${fmtDate(p.publicDataAt)}` : ""}.</div>}
          </Card>
        </div>
      )}
      {tab === "atividades" && <Card><ActivityList filter={{ personId: p.id }} includeClosed /></Card>}
      {tab === "documentos" && <DocumentsPanel ctx={{ personId: p.id }} />}
      {dlg === "edit" && <PersonForm person={p} onClose={() => setDlg(null)} />}
      {dlg === "case" && <NewCaseModal person={{ id: p.id, name: p.name }} onClose={() => setDlg(null)} />}
      {dlg === "process" && <NewProcessModal clientRef={{ id: p.id, name: p.name }} onClose={() => setDlg(null)} />}
      {dlg === "act" && <NewActivityModal defaults={{ ctx: { kind: "pessoa", id: p.id, label: p.name } }} onClose={() => setDlg(null)} />}
      <div className="muted small"><Link to="/pessoas">← Pessoas</Link></div>
    </div>
  );
}
